import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { SelectButton } from "primereact/selectbutton";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  catPrecheck,
  icmRequestStatus,
  initiateIcmRequest,
  catResults,
  icmJobHistory,
  downloadAllIcmArtifacts,
  initiateRmsRequest,
  rmsRequestStatus,
  rmsJobHistory,
  downloadAllRmsArtifacts,
  initiateRmsEltRequest,
  rmsEltRequestStatus,
  rmsEltJobHistory,
  initiateSpatialKatRequest,
  spatialKatRequestStatus,
  spatialKatJobHistory,
  downloadAllSpatialKatArtifacts,
} from "../../../api/routes";
import { useCatModelPolling } from "./useCatModelPolling";
import { SafeReportResponse } from "../Conversion";
import styles from "./CatCard.module.css";
import {
  WorkStatus,
  CatResultsResponse,
  CatJobHistoryItem,
  CatPrecheckResponse,
} from "../../../client/types.gen";
import { useToast } from "../../../context-providers/Toast";
import { downloadBlob } from "../../../util";
import { IfFeatureEnabled, useFeatureEnabled } from "../../../IfFeatureEnabled";
import { useLocalUser } from "../data";
import { SelectedModel } from "./types";
import { LayerPerilTab } from "./LayerPerilTab";
import { EpCurvesTab } from "./EpCurvesTab";
import { AalDriversTab } from "./AalDriversTab";
import { RunHistoryTab } from "./RunHistoryTab";

interface CatCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

const LARGE_CAT_JOB_THRESHOLD = 5000;

export const CatCard: React.FC<CatCardProps> = ({ specialtyPropertyInfo }) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [icmResultsData, setIcmResultsData] =
    useState<CatResultsResponse | null>(null);
  const [rmsResultsData, setRmsResultsData] =
    useState<CatResultsResponse | null>(null);
  const [rmsEltResultsData, setRmsEltResultsData] =
    useState<CatResultsResponse | null>(null);
  const [spatialKatResultsData, setSpatialKatResultsData] =
    useState<CatResultsResponse | null>(null);
  const [jobHistory, setJobHistory] = useState<CatJobHistoryItem[]>([]);
  const [viewingBatchId, setViewingBatchId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<SelectedModel>("rms_elt");
  const toast = useToast();
  const user = useLocalUser();
  const catModelOverrideAllowed = useFeatureEnabled(
    "cat-model-override-allowed",
    undefined,
    user
  );
  const rmsLegacyEnabled = useFeatureEnabled(
    "show-rms-legacy-tab",
    undefined,
    user
  );
  const largeCatJobsAllowed = useFeatureEnabled(
    "large-cat-jobs-allowed",
    undefined,
    user
  );
  const numLocations = specialtyPropertyInfo.precomputed_metadata.num_locations;
  const isLargeJob = numLocations > LARGE_CAT_JOB_THRESHOLD;
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);
  const [showLargeJobBlockedDialog, setShowLargeJobBlockedDialog] =
    useState(false);

  const guardLargeJob = (action: () => void) => {
    if (isLargeJob && !largeCatJobsAllowed) {
      setShowLargeJobBlockedDialog(true);
      return;
    }
    action();
  };
  const [precheck, setPrecheck] = useState<CatPrecheckResponse | null>(null);
  const localUser = useLocalUser();
  const reportId = specialtyPropertyInfo.report_id;

  const handleJobHistoryUpdate = useCallback(
    (modelType: string, jobs: CatJobHistoryItem[]) => {
      setJobHistory((prev) => {
        const otherJobs = prev.filter((j) => j.type !== modelType);
        return [...otherJobs, ...jobs].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );
      });
    },
    []
  );

  const icmPolling = useCatModelPolling({
    modelType: "icm",
    displayName: "ICM",
    reportId,
    toast,
    initiateRequest: initiateIcmRequest,
    checkStatus: icmRequestStatus,
    fetchJobHistory: icmJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setIcmResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const rmsPolling = useCatModelPolling({
    modelType: "rms",
    displayName: "RMS (Legacy)",
    reportId,
    toast,
    initiateRequest: (rid) => initiateRmsRequest(rid, catModelOverrideAllowed),
    checkStatus: rmsRequestStatus,
    fetchJobHistory: rmsJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setRmsResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
    showErrorDetail: true,
  });

  const rmsEltPolling = useCatModelPolling({
    modelType: "rms_elt",
    displayName: "RMS",
    reportId,
    toast,
    initiateRequest: initiateRmsEltRequest,
    checkStatus: rmsEltRequestStatus,
    fetchJobHistory: rmsEltJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setRmsEltResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const spatialKatPolling = useCatModelPolling({
    modelType: "spatialkat",
    displayName: "SpatialKat",
    reportId,
    toast,
    initiateRequest: initiateSpatialKatRequest,
    checkStatus: spatialKatRequestStatus,
    fetchJobHistory: spatialKatJobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setSpatialKatResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
    showErrorDetail: true,
  });

  // Load existing results and check for pending jobs on mount
  useEffect(() => {
    const loadExistingResults = async () => {
      try {
        const [catRes, icmHist, rmsHist, rmsEltHist, skHist, precheckRes] =
          await Promise.all([
            catResults(specialtyPropertyInfo.report_id).catch(() => null),
            icmJobHistory(specialtyPropertyInfo.report_id),
            rmsJobHistory(specialtyPropertyInfo.report_id),
            rmsEltJobHistory(specialtyPropertyInfo.report_id),
            spatialKatJobHistory(specialtyPropertyInfo.report_id),
            catPrecheck(specialtyPropertyInfo.report_id).catch(() => null),
          ]);

        setPrecheck(precheckRes);

        if (catRes?.icm?.air?.aggregate?.layers?.length) {
          setIcmResultsData(catRes);
        }

        if (catRes?.rms?.aggregate?.layers?.length) {
          setRmsResultsData(catRes);
        }

        if (catRes?.spatialkat?.aggregate?.layers?.length) {
          setSpatialKatResultsData(catRes);
        }

        if (catRes?.rms_elt?.aggregate?.layers?.length) {
          setRmsEltResultsData(catRes);
        }

        // Set default selected model: prefer RMS, then RMS Legacy, then ICM
        if (catRes?.rms_elt?.aggregate?.layers?.length) {
          setSelectedModel("rms_elt");
        } else if (catRes?.rms?.aggregate?.layers?.length) {
          setSelectedModel("rms");
        } else if (catRes?.icm?.air?.aggregate?.layers?.length) {
          setSelectedModel("icm");
        }

        // Combine job histories
        const allJobs = [
          ...(icmHist.jobs ?? []),
          ...(rmsHist.jobs ?? []),
          ...(rmsEltHist.jobs ?? []),
          ...(skHist.jobs ?? []),
        ].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );

        setJobHistory(allJobs);

        // Resume polling for any active jobs
        const getActiveIds = (hist: { jobs?: CatJobHistoryItem[] | null }) =>
          (hist.jobs ?? [])
            .filter(
              (job) =>
                job.status === WorkStatus.PENDING ||
                job.status === WorkStatus.IN_PROGRESS
            )
            .map((job) => job.batch_id);

        const activeIcmIds = getActiveIds(icmHist);
        const activeRmsIds = getActiveIds(rmsHist);
        const activeRmsEltIds = getActiveIds(rmsEltHist);
        const activeSkIds = getActiveIds(skHist);

        if (activeIcmIds.length > 0) {
          icmPolling.setPendingRequestIds(activeIcmIds);
        }
        if (activeRmsIds.length > 0) {
          rmsPolling.setPendingRequestIds(activeRmsIds);
        }
        if (activeRmsEltIds.length > 0) {
          rmsEltPolling.setPendingRequestIds(activeRmsEltIds);
        }
        if (activeSkIds.length > 0) {
          spatialKatPolling.setPendingRequestIds(activeSkIds);
        }
      } catch (error) {
        console.log("No existing CAT results found");
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadExistingResults();
  }, [specialtyPropertyInfo.report_id]);

  const rmsNeedsConfirmation =
    (precheck?.rms.warnings.length ?? 0) > 0 && !precheck?.rms.blocked;

  const handleInitiateRmsRequest = async () => {
    if (isLargeJob && !largeCatJobsAllowed) {
      setShowLargeJobBlockedDialog(true);
      return;
    }
    if (
      catModelOverrideAllowed &&
      rmsNeedsConfirmation &&
      !showOverrideWarning
    ) {
      setShowOverrideWarning(true);
      return;
    }
    setShowOverrideWarning(false);
    await rmsPolling.handleInitiate();
  };

  const layerInfoById = useMemo(
    () =>
      new Map(
        specialtyPropertyInfo.report_json.layer_info.map((layer) => [
          layer.layer_id!,
          layer,
        ])
      ),
    [specialtyPropertyInfo]
  );

  const hasIcmResults =
    (icmResultsData?.icm?.air?.aggregate?.layers?.length ?? 0) > 0;
  const hasRmsResults =
    (rmsResultsData?.rms?.aggregate?.layers?.length ?? 0) > 0;
  const hasRmsEltResults =
    (rmsEltResultsData?.rms_elt?.aggregate?.layers?.length ?? 0) > 0;
  const hasSpatialKatResults =
    (spatialKatResultsData?.spatialkat?.aggregate?.layers?.length ?? 0) > 0;
  const hasResults =
    hasIcmResults || hasRmsResults || hasRmsEltResults || hasSpatialKatResults;

  const handleViewJobResults = async (job: CatJobHistoryItem) => {
    try {
      const results = await catResults(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      if (job.type === "icm") {
        setIcmResultsData(results);
      } else if (job.type === "rms_elt") {
        setRmsEltResultsData(results);
      } else if (job.type === "spatialkat") {
        setSpatialKatResultsData(results);
      } else {
        setRmsResultsData(results);
      }
      setViewingBatchId(job.batch_id);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load results for this job.",
      });
    }
  };

  const handleResetToDefaultResults = async () => {
    try {
      const results = await catResults(specialtyPropertyInfo.report_id);
      setIcmResultsData(results);
      setRmsResultsData(results);
      setRmsEltResultsData(results);
      setSpatialKatResultsData(results);
      setViewingBatchId(null);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load default results.",
      });
    }
  };

  const handleDownloadAll = async (job: CatJobHistoryItem) => {
    try {
      const downloadFn =
        job.type === "icm"
          ? downloadAllIcmArtifacts
          : job.type === "spatialkat"
            ? downloadAllSpatialKatArtifacts
            : downloadAllRmsArtifacts;
      const { data, filename } = await downloadFn(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      downloadBlob(filename ?? `${job.type}_all_${job.batch_id}.zip`, data);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Could not download the artifacts zip.",
      });
    }
  };

  const isPending =
    icmPolling.isPending ||
    rmsPolling.isPending ||
    rmsEltPolling.isPending ||
    spatialKatPolling.isPending;

  const pendingMessage = (() => {
    const active: string[] = [];
    if (icmPolling.isPending) active.push("ICM");
    if (rmsPolling.isPending) active.push("RMS (Legacy)");
    if (rmsEltPolling.isPending) active.push("RMS");
    if (spatialKatPolling.isPending) active.push("SpatialKat");
    if (active.length === 0) return "";
    return `${active.join(" and ")} analysis in progress...`;
  })();

  const topLocationsByPeril =
    selectedModel === "rms_elt"
      ? rmsResultsData?.rms_elt?.top_locations_by_peril
      : rmsResultsData?.rms?.top_locations_by_peril;

  return (
    <Card
      title={
        <div className={styles.titleRow}>
          <span>Catastrophe Modeling</span>
          {precheck && (
            <CatPrecheckBanner
              precheck={precheck}
              selectedModel={selectedModel}
            />
          )}
        </div>
      }
      className={styles.catCard}
    >
      <div className={styles.content}>
        {isPending && (
          <Message
            severity="info"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <ProgressSpinner
                  className={styles.spinnerSmall}
                  strokeWidth="4"
                />
                <span>{pendingMessage}</span>
              </div>
            }
          />
        )}

        {viewingBatchId && (
          <Message
            severity="warn"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <span>
                  Viewing results from a historical run — these are not the
                  active results.
                </span>
                <Button
                  label="Reset to default"
                  text
                  size="small"
                  onClick={handleResetToDefaultResults}
                />
              </div>
            }
          />
        )}

        <div style={{ marginBottom: "16px" }}>
          <SelectButton
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.value)}
            options={[
              { label: "RMS", value: "rms_elt" },
              ...(rmsLegacyEnabled
                ? [{ label: "RMS (Legacy)", value: "rms" as const }]
                : []),
              { label: "KatRisk", value: "spatialkat" },
              { label: "ICM", value: "icm" },
            ]}
          />
        </div>
        <div
          className={styles.buttonContainer}
          style={{ marginBottom: "16px" }}
        >
          {selectedModel === "icm" ? (
            <IfFeatureEnabled feature="show-icm-start-button" user={localUser}>
              <Button
                label={hasIcmResults ? "Re-run ICM" : "Run ICM"}
                icon="pi pi-play"
                onClick={icmPolling.handleInitiate}
                loading={icmPolling.isInitiateLoading}
                disabled={
                  icmPolling.isPending || !precheck || precheck.icm.blocked
                }
              />
            </IfFeatureEnabled>
          ) : selectedModel === "rms_elt" ? (
            <Button
              label={hasRmsEltResults ? "Re-run RMS" : "Run RMS"}
              icon="pi pi-play"
              onClick={() => guardLargeJob(rmsEltPolling.handleInitiate)}
              loading={rmsEltPolling.isInitiateLoading}
              disabled={
                rmsEltPolling.isPending ||
                !precheck ||
                precheck.rms.blocked ||
                (!catModelOverrideAllowed && rmsNeedsConfirmation)
              }
            />
          ) : selectedModel === "spatialkat" ? (
            <Button
              label={
                hasSpatialKatResults
                  ? "Re-run KatRisk Flood"
                  : "Run KatRisk Flood"
              }
              icon="pi pi-play"
              onClick={spatialKatPolling.handleInitiate}
              loading={spatialKatPolling.isInitiateLoading}
              disabled={
                spatialKatPolling.isPending ||
                !precheck ||
                precheck.spatialkat.blocked
              }
            />
          ) : (
            <Button
              label={hasRmsResults ? "Re-run RMS (Legacy)" : "Run RMS (Legacy)"}
              icon="pi pi-play"
              onClick={handleInitiateRmsRequest}
              loading={rmsPolling.isInitiateLoading}
              disabled={
                rmsPolling.isPending ||
                !precheck ||
                precheck.rms.blocked ||
                (!catModelOverrideAllowed && rmsNeedsConfirmation)
              }
            />
          )}
        </div>

        {hasResults || jobHistory.length > 0 ? (
          <div className={styles.resultsContainer}>
            <TabView>
              <TabPanel header="By Layer and Peril">
                <LayerPerilTab
                  selectedModel={selectedModel}
                  icmResultsData={icmResultsData}
                  rmsResultsData={rmsResultsData}
                  rmsEltResultsData={rmsEltResultsData}
                  spatialKatResultsData={spatialKatResultsData}
                  layerInfoById={layerInfoById}
                />
              </TabPanel>

              {(selectedModel === "rms" || selectedModel === "spatialkat") && (
                <TabPanel header="EP Curves">
                  <EpCurvesTab
                    selectedModel={selectedModel}
                    rmsResultsData={rmsResultsData}
                    spatialKatResultsData={spatialKatResultsData}
                  />
                </TabPanel>
              )}

              {(selectedModel === "rms" || selectedModel === "rms_elt") &&
                topLocationsByPeril &&
                Object.keys(topLocationsByPeril).length > 0 && (
                  <TabPanel header="AAL Drivers">
                    <AalDriversTab
                      topLocationsByPeril={topLocationsByPeril}
                      reportId={reportId}
                    />
                  </TabPanel>
                )}

              <TabPanel header="Run History">
                <RunHistoryTab
                  jobHistory={jobHistory}
                  selectedModel={selectedModel}
                  viewingBatchId={viewingBatchId}
                  onViewJobResults={handleViewJobResults}
                  onDownloadAll={handleDownloadAll}
                />
              </TabPanel>
            </TabView>
          </div>
        ) : isInitialLoading ? (
          <div className={styles.loadingContainer}>
            <ProgressSpinner />
          </div>
        ) : !isPending ? (
          <Message
            severity="info"
            text="No CAT results available yet. Run an analysis to generate results."
            className={styles.messageMargin}
          />
        ) : null}
      </div>

      {/* Override Warning Dialog */}
      <Dialog
        header="Cat Model Warning"
        visible={showOverrideWarning}
        onHide={() => setShowOverrideWarning(false)}
        draggable={false}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={() => setShowOverrideWarning(false)}
            />
            <Button
              label="Proceed"
              icon="pi pi-play"
              severity="warning"
              onClick={handleInitiateRmsRequest}
              loading={rmsPolling.isInitiateLoading}
            />
          </div>
        }
      >
        <p>{precheck?.rms.warnings.join(" ")}</p>
      </Dialog>

      <Dialog
        header="Large Submission"
        visible={showLargeJobBlockedDialog}
        onHide={() => setShowLargeJobBlockedDialog(false)}
        draggable={false}
        footer={
          <Button
            label="OK"
            onClick={() => setShowLargeJobBlockedDialog(false)}
          />
        }
      >
        <p>
          This submission has <b>{numLocations.toLocaleString()}</b> locations,
          which exceeds the {LARGE_CAT_JOB_THRESHOLD.toLocaleString()} location
          limit for running CAT jobs. Please ping John, Sean, or Andrew to run
          this for you.
        </p>
      </Dialog>
    </Card>
  );
};

const VENDOR_PRECHECK_KEY: Record<SelectedModel, "icm" | "rms" | "spatialkat"> =
  {
    icm: "icm",
    rms: "rms",
    rms_elt: "rms",
    spatialkat: "spatialkat",
  };

const CatPrecheckBanner: React.FC<{
  precheck: CatPrecheckResponse;
  selectedModel: SelectedModel;
}> = ({ precheck, selectedModel }) => {
  const vendor = precheck[VENDOR_PRECHECK_KEY[selectedModel]];
  const warnings = [...precheck.warnings, ...vendor.warnings];
  if (!warnings.length) return null;

  return (
    <Message
      severity="warn"
      className={styles.precheckMessage}
      content={<span>{warnings.join(" ")}</span>}
    />
  );
};
